import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { LogOut, Plus, Home, Search, User as UserIcon, BadgeCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { NotificationBell } from "@/components/NotificationBell";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isCustomer = user?.role === "customer";
  const isRemovalist = user?.role === "removalist";
  const isAdmin = user?.role === "admin";

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-[rgba(255,255,255,0.92)] dark:bg-[rgba(15,23,42,0.92)] backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={isAdmin ? "/admin" : "/dashboard"} data-testid="navbar-brand-logo" className="flex items-center gap-2 group">
          <BrandLogo />
        </Link>

        {user && (
          <nav className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(isAdmin ? "/admin" : "/dashboard")} data-testid="nav-home-btn" className="gap-2">
              <Home className="h-4 w-4" /> <span className="hidden sm:inline">Home</span>
            </Button>
            {isCustomer && (
              <Button size="sm" onClick={() => navigate("/post-job")} data-testid="post-job-nav-btn" className="gap-2 bg-kinetic hover:bg-kinetic-hover text-white">
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Post Job</span>
              </Button>
            )}
            {isRemovalist && (
              <Button size="sm" onClick={() => navigate("/dashboard")} data-testid="browse-jobs-nav-btn" className="gap-2 bg-kinetic hover:bg-kinetic-hover text-white">
                <Search className="h-4 w-4" /> <span className="hidden sm:inline">Browse Jobs</span>
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" onClick={() => navigate("/admin")} data-testid="admin-console-nav-btn" className="gap-2 bg-kinetic hover:bg-kinetic-hover text-white">
                <ShieldCheck className="h-4 w-4" /> Admin Console
              </Button>
            )}
            {!isAdmin && <NotificationBell />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="outline-none">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarFallback className="bg-asphalt text-white text-sm font-semibold">
                      {user.name?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-card">
                <DropdownMenuLabel className="flex flex-col gap-1">
                  <span className="font-semibold truncate">{user.name}</span>
                  <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                  <div className="flex items-center gap-1 mt-1">
                    <Badge variant="outline" className="capitalize text-[10px]">{user.role}</Badge>
                    {user.role === "removalist" && user.is_verified && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] gap-1"><BadgeCheck className="h-3 w-3" /> Verified</Badge>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "removalist" && (
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile-btn" className="gap-2">
                    <UserIcon className="h-4 w-4" /> Business Profile
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="gap-2 text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        )}
      </div>
    </header>
  );
}
